import { Component, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { KeycloakAuthService } from './auth/keycloak.service';
import { PENDING_WORKFLOW_LINK_KEY } from './workflow-deep-link';
import { RefreshStateService } from './services/refresh-state/refresh-state.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnDestroy {
  private readonly staleSessionLogoutThresholdMs = 5 * 60 * 1000;
  private routerSubscription?: Subscription;

  constructor(
    private router: Router,
    private keycloakAuthService: KeycloakAuthService,
    private refreshState: RefreshStateService
  ) { }

  async logout() {
    localStorage.removeItem("companyId");
    if (this.keycloakAuthService.isAuthenticated()) {
      await this.keycloakAuthService.logout();
      return;
    }
  }

  justEntered = true;

  ngOnInit(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.refreshState.saveNavigationState(event.urlAfterRedirects, history.state);
      });

    const storedDateStr = localStorage.getItem("beat");
    if (storedDateStr && this.justEntered) {
      // Check timers
      const currentDateStr = new Date().toString();
      const storedDate: any = new Date(storedDateStr);
      const currentDate: any = new Date(currentDateStr);

      const timeDifference = currentDate - storedDate;

      if (
        window.location.hash.includes('#/public-form') || window.location.href.includes("/assets")
      ) {
        this.justEntered = false;
      } else if (
        !this.isAuthCallbackNavigation() &&
        timeDifference > this.staleSessionLogoutThresholdMs
      ) {
        this.logout();
      }
    }

    this.justEntered = false;
    // Doing it here because it stops ticking if you reload too fast
    localStorage.setItem("beat", new Date().toString());
    this.startTimer();

    this.openPendingWorkflowLink();
  }

  /**
   * D3: Keycloak returns its auth response in the URL fragment and clears it, which takes the
   * hash route with it. main.ts stashes the intended workflow link before that happens; replay
   * it here so an emailed link still lands on the form after authentication.
   */
  private openPendingWorkflowLink(): void {
    const target = sessionStorage.getItem(PENDING_WORKFLOW_LINK_KEY);
    if (!target) return;
    sessionStorage.removeItem(PENDING_WORKFLOW_LINK_KEY);

    if (!this.keycloakAuthService.isAuthenticated()) return;

    void this.router.navigateByUrl(target, { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  startTimer() {
    const item = setInterval(() => {
      const currentDate = new Date().toString();
      localStorage.setItem("beat", currentDate);
    }, 700);
  }

  private isAuthCallbackNavigation(): boolean {
    const search = window.location.search;
    return search.includes('code=') || search.includes('state=') || search.includes('session_state=');
  }

  title = 'smartforms-portal';
}
